import {
	ComponentFunctionRequirementInstanceMetadataSchema,
	ComponentFunctionRequirementResultMetadataSchema,
	ComponentResourceRequirementInstanceMetadataSchema,
	ComponentResourceRequirementResultMetadataSchema,
	convertBoundaryLevelToRequirementStatus,
	RequirementResultType,
} from '@cadstrom/sigma-engine-shared';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import { DataGridPremium } from '@mui/x-data-grid-premium';
import { useMemo } from 'react';

import { AnalysisResultsModuleChip } from '../components/analysis-results-module-chip.js';
import { StatusBadge } from '../components/status-badge.js';

import type {
	BoundaryLevel,
	IInsightError,
	ModelWithNetStats,
	RequirementInstanceResult,
} from '@cadstrom/sigma-engine-shared';
import type { GridColDef } from '@mui/x-data-grid-premium';

type ComponentFunctionDetailPanelProps = {
	row: RequirementInstanceResult;
	loading?: boolean;
	projectModel?: ModelWithNetStats;
	/**
	 * Optional click-through for module ref-des chip menus. Omit when the panel
	 * is mounted outside the analysis-results grid (e.g. inside a V2 activity
	 * timeline payload) — the menu item is dropped in that case.
	 */
	handleAddModuleFilter?: (moduleReferenceDesignator: string) => void;
};

interface InsightTableRow {
	id: string;
	status: BoundaryLevel;
	errorId: string;
	message: string;
}

export function ComponentFunctionDetailPanel({
	row,
	loading = false,
	handleAddModuleFilter,
}: ComponentFunctionDetailPanelProps) {
	// Determine if this is a function or resource validation
	const isFunction = row.typeId === RequirementResultType.ComponentFunction;

	// Parse metadata using the appropriate schema based on type
	const instanceMetadataResult =
		isFunction ?
			ComponentFunctionRequirementInstanceMetadataSchema.safeParse(row.instanceMetadata)
		:	ComponentResourceRequirementInstanceMetadataSchema.safeParse(row.instanceMetadata);

	const resultMetadataResult =
		isFunction ?
			ComponentFunctionRequirementResultMetadataSchema.safeParse(row.resultMetadata)
		:	ComponentResourceRequirementResultMetadataSchema.safeParse(row.resultMetadata);

	const instanceMetadata = instanceMetadataResult.success ? instanceMetadataResult.data : undefined;
	const resultMetadata = resultMetadataResult.success ? resultMetadataResult.data : undefined;

	const insightRows = useMemo<InsightTableRow[]>(() => {
		if (!resultMetadata) {
			return [];
		}
		return resultMetadata.insights.map((insight: IInsightError) => ({
			id: insight.id,
			status: insight.level,
			errorId: insight.errorId,
			message: insight.message,
		}));
	}, [resultMetadata]);

	const insightColumns = useMemo<GridColDef[]>(
		() => [
			{
				field: 'status',
				headerName: 'Status',
				width: 100,
				renderCell: (params) => {
					const level = params.value as BoundaryLevel;
					const status = convertBoundaryLevelToRequirementStatus(level);
					return (
						<div className='flex h-full items-center gap-1'>
							<StatusBadge status={status} size='small' />
						</div>
					);
				},
			},
			{
				field: 'errorId',
				headerName: 'Error ID',
				flex: 1,
				minWidth: 150,
				renderCell: (params) => <div className='flex h-full items-center'>{params.value as string}</div>,
			},
			{
				field: 'message',
				headerName: 'Message',
				flex: 2,
				minWidth: 300,
				renderCell: (params) => <div className='py-2 whitespace-normal'>{params.value as string}</div>,
			},
		],
		[],
	);

	if (!instanceMetadataResult.success) {
		console.error('[ComponentFunctionDetailPanel]_InstanceMetadataParseError', {
			error: instanceMetadataResult.error,
			rawData: row.instanceMetadata,
		});
		return (
			<div className='w-full overflow-auto bg-gray-a5 p-4'>
				<p className='text-gray-a9 italic'>Error parsing instance metadata</p>
			</div>
		);
	}

	if (!resultMetadataResult.success) {
		console.error('[ComponentFunctionDetailPanel]_ResultMetadataParseError', {
			error: resultMetadataResult.error,
			rawData: row.resultMetadata,
		});
		return (
			<div className='w-full overflow-auto bg-gray-a5 p-4'>
				<p className='text-gray-a9 italic'>Error parsing result metadata</p>
			</div>
		);
	}

	// At this point, both metadata objects are guaranteed to be defined
	if (!instanceMetadata || !resultMetadata) {
		return (
			<div className='w-full overflow-auto bg-gray-a5 p-4'>
				<p className='text-gray-a9 italic'>Metadata is not available. This can indicate a data issue.</p>
			</div>
		);
	}

	return (
		<div className='flex flex-col gap-4 p-2'>
			<Paper className='p-2'>
				{instanceMetadata.moduleName && (
					<div className='mb-4 flex items-center gap-2'>
						<AnalysisResultsModuleChip
							moduleReferenceDesignator={instanceMetadata.moduleName}
							handleAddModuleFilter={handleAddModuleFilter}
						/>
					</div>
				)}

				<div className='mb-4 flex'>
					<h4 className='text-lg font-semibold'>{isFunction ? 'Function ID' : 'Resource ID'}:&nbsp;</h4>
					<span>
						{'componentFunctionId' in instanceMetadata ?
							instanceMetadata.componentFunctionId
						:	instanceMetadata.componentResourceId}
					</span>
				</div>

				<div className='mt-4'>
					<h4 className='mb-2 text-lg font-semibold'>Insights</h4>
					{insightRows.length === 0 ?
						<div className='py-2 text-center'>
							<span className='text-gray-a9'>No errors detected</span>
						</div>
					:	<DataGridPremium
							rows={insightRows}
							columns={insightColumns}
							loading={loading}
							hideFooter
							disableColumnMenu
							disableRowSelectionOnClick
							autoHeight
							getRowHeight={() => 'auto'}
						/>
					}
				</div>
			</Paper>
		</div>
	);
}
